import React, { type ReactNode } from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {
  adminMobileCard,
  adminSurface,
  adminTableHeadRow,
  adminTableRowSx,
} from "../../styles/adminUiTokens";

export interface AdminTableColumn<T> {
  id: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  hideOnMobile?: boolean;
  /** Used as card title on mobile; defaults to first visible column */
  mobilePrimary?: boolean;
  mobileLabel?: string;
  renderCell: (row: T) => ReactNode;
}

export interface AdminDataTableProps<T> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  loading?: boolean;
  loadingRows?: number;
  footer?: ReactNode;
  /** Custom header cell (e.g. sort labels) */
  renderHeaderCell?: (column: AdminTableColumn<T>) => ReactNode;
}

function AdminDataTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyMessage = "No results found",
  loading = false,
  loadingRows = 5,
  footer,
  renderHeaderCell,
}: AdminDataTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const mobileColumns = columns.filter((c) => !c.hideOnMobile);
  const primaryColumn =
    mobileColumns.find((c) => c.mobilePrimary) ?? mobileColumns[0];

  const renderMobileCards = () => {
    if (loading) {
      return (
        <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 } }}>
          {[...Array(loadingRows)].map((_, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Skeleton width="70%" />
              <Skeleton width="50%" sx={{ mt: 1 }} />
              <Skeleton width="40%" sx={{ mt: 0.5 }} />
            </Paper>
          ))}
        </Stack>
      );
    }

    if (rows.length === 0) {
      return (
        <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 } }}>
        {rows.map((row, index) => (
          <Paper
            key={getRowId(row)}
            elevation={0}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{
              ...adminMobileCard,
              bgcolor: index % 2 === 1 ? "grey.50" : "background.paper",
              ...(onRowClick ? {} : { cursor: "default", "&:hover": {} }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {primaryColumn && (
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 1, wordBreak: "break-word" }}
                  >
                    {primaryColumn.renderCell(row)}
                  </Typography>
                )}
                <Stack spacing={0.75}>
                  {mobileColumns
                    .filter((c) => c.id !== primaryColumn?.id)
                    .map((col) => (
                      <Box
                        key={col.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          {col.mobileLabel ??
                            (typeof col.header === "string"
                              ? col.header
                              : col.id)}
                        </Typography>
                        <Box
                          sx={{
                            textAlign: "right",
                            fontSize: "0.875rem",
                            wordBreak: "break-word",
                          }}
                        >
                          {col.renderCell(row)}
                        </Box>
                      </Box>
                    ))}
                </Stack>
              </Box>
              {onRowClick && (
                <ChevronRight
                  sx={{ color: "text.disabled", flexShrink: 0, mt: 0.25 }}
                />
              )}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderDesktopTable = () => (
    <TableContainer>
      <Table size="medium">
        <TableHead>
          <TableRow sx={adminTableHeadRow}>
            {columns.map((col) => (
              <TableCell key={col.id} align={col.align ?? "left"}>
                {renderHeaderCell ? renderHeaderCell(col) : col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(loadingRows)].map((_, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow
                key={getRowId(row)}
                hover={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  ...adminTableRowSx(index),
                  ...(onRowClick ? { cursor: "pointer" } : {}),
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align ?? "left"}>
                    {col.renderCell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper elevation={0} sx={adminSurface}>
      {isMobile ? renderMobileCards() : renderDesktopTable()}
      {footer}
    </Paper>
  );
}

export default AdminDataTable;
